import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import type { GamePassUserBetsResponse } from "./taskBetTypes";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;

const buildGamePassUserBetsUrl = (userId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_BETS.replace("{user_id}", userId);

const buildGamePassUserCasinoBetsUrl = (userId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_CASINO_BETS.replace("{user_id}", userId);

const fetchGamePassUserBets = async (
  userId: string,
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
): Promise<GamePassUserBetsResponse> => {
  const response = await getPrivateInstance().get<GamePassUserBetsResponse>(
    buildGamePassUserBetsUrl(userId),
    { params: { page, limit } },
  );
  return response.data;
};

const fetchGamePassUserCasinoBets = async (
  userId: string,
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
): Promise<GamePassUserBetsResponse> => {
  const response = await getPrivateInstance().get<GamePassUserBetsResponse>(
    buildGamePassUserCasinoBetsUrl(userId),
    { params: { page, limit } },
  );
  return response.data;
};

type UseGetGamePassUserBetsOptions = {
  enabled?: boolean;
  page?: number;
  limit?: number;
};

/**
 * `GET /gamepass/v2/admin/users/{user_id}/bets` — sportsbook bet history for
 * the Monthly Challenges support surface.
 */
export const useGetGamePassUserBets = (
  userId?: string,
  options?: UseGetGamePassUserBetsOptions,
): UseQueryResult<GamePassUserBetsResponse, Error> => {
  const page = options?.page ?? DEFAULT_PAGE;
  const limit = options?.limit ?? DEFAULT_LIMIT;

  return useQuery<GamePassUserBetsResponse, Error>({
    queryKey: ["game-pass", "users", "bets", userId ?? "", page, limit],
    queryFn: () => fetchGamePassUserBets(userId!, page, limit),
    enabled: Boolean(userId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });
};

/**
 * `GET /gamepass/v2/admin/users/{user_id}/casino-bets` — casino bet history.
 */
export const useGetGamePassUserCasinoBets = (
  userId?: string,
  options?: UseGetGamePassUserBetsOptions,
): UseQueryResult<GamePassUserBetsResponse, Error> => {
  const page = options?.page ?? DEFAULT_PAGE;
  const limit = options?.limit ?? DEFAULT_LIMIT;

  return useQuery<GamePassUserBetsResponse, Error>({
    queryKey: ["game-pass", "users", "casino-bets", userId ?? "", page, limit],
    queryFn: () => fetchGamePassUserCasinoBets(userId!, page, limit),
    enabled: Boolean(userId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });
};

export {
  buildGamePassUserBetsUrl,
  buildGamePassUserCasinoBetsUrl,
  fetchGamePassUserBets,
  fetchGamePassUserCasinoBets,
};
